import React, { useEffect, useMemo, useState } from "react";

//import assets
import UserIcon from "../../assets/icons/user.png";

//import helpers/utils/services
import Axios from "../../utils/axios";

const EMPTY_SUMMARY = {
  profile: {},
  pickups: [],
  favourites: [],
  downloads: [],
  achievements: [],
  testimonies: [],
};

const STAT_CONFIG = [
  {
    key: "pickups",
    label: "In Progress",
    description: "Started content the user can continue later.",
  },
  {
    key: "favourites",
    label: "Favourites",
    description: "Content saved or liked by this user.",
  },
  {
    key: "downloads",
    label: "Downloads",
    description: "Offline content downloaded by this user.",
  },
  {
    key: "achievements",
    label: "Achievements",
    description: "Milestones or achievement records earned by this user.",
  },
  {
    key: "testimonies",
    label: "Testimonies",
    description: "Testimonies created or associated with this user.",
  },
];

//function to safely unwrap list data from API responses
const getListFromResponse = (response, nestedKey = null) => {
  const data = response?.data;

  if (nestedKey) {
    return data?.data?.[0]?.[nestedKey] || data?.data?.[nestedKey] || [];
  }

  if (Array.isArray(data?.data)) {
    return data.data;
  }

  if (Array.isArray(data)) {
    return data;
  }

  return [];
};

//function to safely unwrap selected-user scoped nested list data
const getScopedListFromResponse = (response, nestedKey, userAuthId) => {
  const data = response?.data?.data;
  const profileWrapper = Array.isArray(data) ? data[0] : data;

  if (profileWrapper?.userAuthId && profileWrapper.userAuthId !== userAuthId) {
    return [];
  }

  return getListFromResponse(response, nestedKey);
};

//function to pick the best display value
const getDisplayValue = (...values) => {
  return values.find((value) => value !== undefined && value !== null && value !== "") || "Not available";
};

//function to build user display name
const getDisplayName = (profile) => {
  const name = `${profile?.firstName || ""} ${profile?.lastName || ""}`.trim();
  return name || profile?.twgId || profile?.email || "Unknown User";
};

//function to create count cards from summary data
const buildStats = (summary) => {
  return STAT_CONFIG.map((stat) => ({
    ...stat,
    value: summary[stat.key]?.length || 0,
    items: summary[stat.key] || [],
  }));
};

//function to get nested object detail when API joins return arrays
const getFirstJoinedItem = (item, key) => {
  if (Array.isArray(item?.[key])) {
    return item[key][0] || {};
  }

  return item?.[key] || {};
};

//function to resolve best content title from mixed activity records
const getActivityTitle = (item) => {
  const video = getFirstJoinedItem(item, "videoDetails");
  const curriculum = getFirstJoinedItem(item, "curriculumDetails");
  const category = getFirstJoinedItem(item, "categoryDetails");
  const testimony = getFirstJoinedItem(item, "testimonyDetails");

  return getDisplayValue(
    item?.title,
    item?.name,
    item?.video_title,
    item?.curriculum_name,
    item?.category_name,
    item?.testimonial_name,
    item?.lessonTitle,
    item?.slidetitle,
    video?.video_title,
    curriculum?.curriculum_name,
    category?.category_name,
    testimony?.testimonial_name
  );
};

//function to resolve readable content type from activity record
const getActivityType = (item) => {
  const typeMap = {
    1: "Video",
    2: "Curriculum",
    3: "Category",
    4: "Testimony",
    video: "Video",
    curriculum: "Curriculum",
    category: "Category",
    testimony: "Testimony",
  };

  return getDisplayValue(item?.contentType, item?.typeName, typeMap[item?.type], "Content");
};

//function to resolve best available image for activity row
const getActivityImage = (item) => {
  const video = getFirstJoinedItem(item, "videoDetails");
  const curriculum = getFirstJoinedItem(item, "curriculumDetails");
  const category = getFirstJoinedItem(item, "categoryDetails");
  const testimony = getFirstJoinedItem(item, "testimonyDetails");

  return (
    item?.thumbnailUrl ||
    item?.tumbnailUrl ||
    item?.testomonial_rec_tumbnailUrl ||
    video?.tumbnailurls?.[0]?.tumbnailUrl ||
    video?.tumbnailurls?.[0]?.url ||
    curriculum?.bg_Images?.[0]?.url ||
    category?.icon_images?.[0]?.url ||
    testimony?.testomonial_rec_tumbnailUrl ||
    ""
  );
};

//function to resolve best date text for activity row
const getActivityDate = (item) => {
  const value = item?.record_date || item?.createdDate || item?.lastUpdatedDate || item?.testimony_date;
  if (!value) return "Date not available";

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return String(value);
  }

  return parsedDate.toLocaleDateString();
};

//function to resolve best progress text for in-progress activity
const getProgressText = (item) => {
  if (item?.percentagewatched) {
    return `${item.percentagewatched}% watched`;
  }
  if (item?.stoppedat) {
    return `Stopped at ${item.stoppedat}`;
  }
  if (item?.progress) {
    return `${item.progress}% complete`;
  }

  return "";
};

//function to identify missing profile fields for admin snapshot
const getMissingProfileFields = (profile) => {
  const requiredFields = [
    { label: "phone", value: profile?.mobile },
    { label: "country", value: profile?.country },
    { label: "About Me", value: profile?.aboutMe || profile?.bio },
    { label: "DOB", value: profile?.dob },
    { label: "gender", value: profile?.gender },
    { label: "profile picture", value: profile?.profilePicUrl || profile?.profilePic },
  ];

  return requiredFields.filter((field) => !field.value).map((field) => field.label);
};

//function to build admin-readable snapshot copy
const buildAdminSnapshot = (profile, stats, entityName) => {
  const missingFields = getMissingProfileFields(profile);
  const activeStats = stats
    .filter((stat) => stat.value > 0)
    .map((stat) => `${stat.value} ${stat.label.toLowerCase()}`);
  const relationship = `This user follows ${entityName || "this entity"}.`;
  const engagement = activeStats.length
    ? `Engagement includes ${activeStats.join(", ")}.`
    : "No engagement activity is available from the connected APIs yet.";
  const profileStatus = missingFields.length
    ? `Profile is missing ${missingFields.join(", ")}.`
    : "Profile has the core fields completed.";

  return `${relationship} ${engagement} ${profileStatus}`;
};

//function to render a compact summary row
const SummaryRow = ({ label, value }) => (
  <div className="flex flex-col gap-1 rounded-2xl bg-[#151515] px-4 py-3 border border-white/5">
    <span className="text-xs uppercase tracking-[0.18em] text-gray-500">{label}</span>
    <span className="text-sm text-gray-100 break-words">{value}</span>
  </div>
);

//function to render an insight stat card
const StatCard = ({ label, value, isActive, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`text-left rounded-2xl border p-4 transition ${
      isActive
        ? "bg-[#F8BA34]/15 border-[#F8BA34] shadow-[0_0_0_1px_rgba(248,186,52,0.15)]"
        : value > 0
          ? "bg-black/30 border-[#F8BA34]/20 hover:border-[#F8BA34]/70"
          : "bg-black/20 border-white/5 opacity-70"
    }`}
  >
    <div className="text-2xl font-semibold text-[#F8BA34]">{value}</div>
    <div className="text-xs text-gray-400 mt-1">{label}</div>
  </button>
);

//function to render selected analytics item row
const DetailItem = ({ item, statKey }) => {
  const imageUrl = getActivityImage(item);
  const progressText = statKey === "pickups" ? getProgressText(item) : "";

  return (
    <div className="flex gap-3 rounded-2xl bg-black/30 border border-white/5 p-3">
      <div className="h-14 w-16 shrink-0 overflow-hidden rounded-xl bg-[#0F0F0F] border border-white/5">
        {imageUrl ? (
          <img src={imageUrl} alt={getActivityTitle(item)} className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-[10px] text-gray-500">No image</div>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-white">{getActivityTitle(item)}</p>
        <div className="mt-1 flex flex-wrap gap-2 text-xs text-gray-400">
          <span>{getActivityType(item)}</span>
          <span>{getActivityDate(item)}</span>
          {progressText && <span className="text-[#F8BA34]">{progressText}</span>}
        </div>
      </div>
    </div>
  );
};

//function to render analytics drill-down panel
const DetailPanel = ({ stat }) => (
  <div className="mt-5 rounded-2xl bg-[#151515] p-5 border border-white/5">
    <div className="flex items-start justify-between gap-4">
      <div>
        <h3 className="text-lg font-semibold text-white">{stat.label}</h3>
        <p className="mt-1 text-sm text-gray-400">{stat.description}</p>
      </div>
      <span className="rounded-full bg-[#F8BA34]/15 px-3 py-1 text-xs font-semibold text-[#F8BA34]">
        {stat.value} item{stat.value === 1 ? "" : "s"}
      </span>
    </div>
    <div className="mt-4 max-h-72 overflow-y-auto pr-1">
      {stat.items.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {stat.items.map((item, index) => (
            <DetailItem key={item?._id || item?.record_date || `${stat.key}-${index}`} item={item} statKey={stat.key} />
          ))}
        </div>
      ) : (
        <div className="rounded-2xl bg-black/30 border border-white/5 p-4 text-sm text-gray-400">
          No related {stat.label.toLowerCase()} items are available for this user.
        </div>
      )}
    </div>
  </div>
);

//function to render selected user insight modal
const UserInsightModal = ({ isOpen, onClose, user, entityName = "entity" }) => {
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [error, setError] = useState("");
  const [selectedStatKey, setSelectedStatKey] = useState("pickups");

  const userAuthId = user?.userAuthId;

  const mergedProfile = useMemo(() => {
    return {
      ...user,
      ...(summary.profile || {}),
    };
  }, [summary.profile, user]);

  const displayName = useMemo(() => getDisplayName(mergedProfile), [mergedProfile]);

  const stats = useMemo(() => buildStats(summary), [summary]);
  const selectedStat = useMemo(
    () => stats.find((stat) => stat.key === selectedStatKey) || stats[0],
    [selectedStatKey, stats]
  );
  const adminSnapshot = useMemo(
    () => buildAdminSnapshot(mergedProfile, stats, entityName),
    [entityName, mergedProfile, stats]
  );

  //function to reset selected analytics tab when popup opens
  useEffect(() => {
    if (isOpen) {
      setSelectedStatKey("pickups");
    }
  }, [isOpen, userAuthId]);

  //function to fetch available user details for the popup
  useEffect(() => {
    let isMounted = true;

    const fetchUserSummary = async () => {
      if (!isOpen || !userAuthId) {
        return;
      }

      setLoading(true);
      setError("");
      setSummary(EMPTY_SUMMARY);

      const payload = { userid: userAuthId };

      try {
        const [profile, pickups, favourites, downloads, achievements, testimonies] =
          await Promise.allSettled([
            Axios.post("/profile", { userAuthId }),
            Axios.post("/user/getPickups", payload),
            Axios.post("/user/getFav", payload),
            Axios.post("/user/getDownloads", payload),
            Axios.post("/user/getAchievement", payload),
            Axios.post("/testimony/read", payload),
          ]);

        if (!isMounted) {
          return;
        }

        setSummary({
          profile: getListFromResponse(profile.value)?.[0] || {},
          pickups: getListFromResponse(pickups.value, "pickuplefts"),
          favourites: getListFromResponse(favourites.value, "favourites"),
          downloads: getScopedListFromResponse(downloads.value, "downloads", userAuthId),
          achievements: getListFromResponse(achievements.value),
          testimonies: getListFromResponse(testimonies.value),
        });

        const hasFailedSummary = [profile, pickups, favourites, downloads, achievements, testimonies].some(
          (result) => result.status === "rejected"
        );

        if (hasFailedSummary) {
          setError("Some user summary sections could not be loaded.");
        }
      } catch (err) {
        if (isMounted) {
          setError("Unable to load user summary right now.");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchUserSummary();

    return () => {
      isMounted = false;
    };
  }, [isOpen, userAuthId]);

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/80 px-4 py-6">
      <div className="w-full max-w-5xl max-h-[90vh] overflow-hidden rounded-[28px] bg-[#1F1F1F] border border-white/10 shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-white/10 px-6 py-5">
          <div className="flex items-center gap-4 min-w-0">
            <div className="h-16 w-16 shrink-0 rounded-full overflow-hidden border-2 border-[#F8BA34] bg-black">
              <img
                src={mergedProfile?.profilePicUrl || mergedProfile?.profilePic || UserIcon}
                alt={displayName}
                className="h-full w-full object-cover"
              />
            </div>
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-[0.22em] text-[#F8BA34]">User Insight</p>
              <h2 className="text-2xl font-semibold text-white truncate">
                {displayName}
              </h2>
              <p className="text-sm text-gray-400 truncate">
                {getDisplayValue(mergedProfile?.twgId)} - Following {entityName}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full bg-black/50 px-4 py-2 text-sm text-white hover:bg-black"
          >
            Close
          </button>
        </div>

        <div className="max-h-[calc(90vh-96px)] overflow-y-auto px-6 py-6">
          {loading && <div className="mb-4 rounded-2xl bg-black/30 p-4 text-sm text-gray-300">Loading user summary...</div>}

          {error && <div className="mb-4 rounded-2xl bg-[#3A2D13] p-4 text-sm text-[#F8BA34]">{error}</div>}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <SummaryRow label="Email" value={getDisplayValue(mergedProfile?.email)} />
            <SummaryRow label="Mobile" value={getDisplayValue(mergedProfile?.mobile)} />
            <SummaryRow label="Language" value={getDisplayValue(mergedProfile?.language_prefference)} />
            <SummaryRow label="Country" value={getDisplayValue(mergedProfile?.country)} />
            <SummaryRow label="Gender" value={getDisplayValue(mergedProfile?.gender)} />
            <SummaryRow label="Preference" value={getDisplayValue(mergedProfile?.preference)} />
          </div>

          <div className="mt-5 rounded-2xl bg-[#151515] p-5 border border-white/5">
            <p className="text-xs uppercase tracking-[0.18em] text-gray-500 mb-2">About</p>
            <p className="text-sm leading-6 text-gray-200">
              {getDisplayValue(mergedProfile?.aboutMe, mergedProfile?.bio)}
            </p>
          </div>

          <div className="mt-5 grid grid-cols-2 md:grid-cols-5 gap-4">
            {stats.map((stat) => (
              <StatCard
                key={stat.key}
                label={stat.label}
                value={stat.value}
                isActive={selectedStatKey === stat.key}
                onClick={() => setSelectedStatKey(stat.key)}
              />
            ))}
          </div>

          <DetailPanel stat={selectedStat} />

          <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="rounded-2xl bg-[#151515] p-5 border border-white/5">
              <h3 className="text-lg font-semibold text-white">Relationship</h3>
              <div className="mt-4 flex flex-wrap gap-2">
                {mergedProfile?.isHomeChurch && (
                  <span className="rounded-full bg-[#F8BA34]/20 px-3 py-1 text-xs text-[#F8BA34]">
                    Home Church
                  </span>
                )}
                {mergedProfile?.isLikedChurch && (
                  <span className="rounded-full bg-green-500/15 px-3 py-1 text-xs text-green-300">
                    Favourite Church
                  </span>
                )}
                {!mergedProfile?.isHomeChurch && !mergedProfile?.isLikedChurch && (
                  <span className="text-sm text-gray-400">No preference flags available.</span>
                )}
              </div>
            </div>

            <div className="rounded-2xl bg-[#151515] p-5 border border-white/5">
              <h3 className="text-lg font-semibold text-white">Admin Snapshot</h3>
              <p className="mt-3 text-sm leading-6 text-gray-300">
                {adminSnapshot}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserInsightModal;
